// World Boss: one boss a day for the whole server (Cole 2026-10-15 and 2026-10-20). It can't be killed. Each trainer
// gets WB.tries attacks a UTC day with up to five creatures; a trainer's score for the day is their best attack, and
// the server's total is everyone's best added up. The total climbs through WB.tiers; when the day ends, every trainer
// who attacked is paid by their own best and the tier the whole server reached, so everyone wants everyone strong.
//
// The fight is the Bosses page's duel (shared/boss-duel.ts) with a boss that never falls: the team attacks until it is
// knocked out or the clock runs out, and the score is the damage dealt. Every creature and the boss fight at
// WB.level, up or down, as ranked and the Halls do (Cole 2026-10-20), so the score measures the team (species,
// rolls, stars, relics, counters, order), not hours played, and a new trainer's attack counts.
//
// Opens when WB.open is set in code (Cole 2026-10-20: month 2 or 3, once player numbers allow); until then
// the page shows "Coming soon" and test
// accounts can try it without counting. Rewards are coins (scaled by trainer level like hunt kills), Revives, and a key
// for the top three (the day's boss's, or the highest one their hunts drop): no diamonds, gems or odds items.
import { bossFighter, creatureFighter, duel } from './boss-duel.ts';
import { killCoins } from './xp.ts';
import { BOSSES } from './species.ts';
import { BOSS_TOKEN } from './items.ts';
import { REGIONS } from './grounds.ts';
import type { Fighter } from './combat.ts';
import type { Rng } from './rng.ts';
import type { BossResult, CreatureDTO } from './protocol.ts';

export const WB = {
  /**
   * Open for everyone. Off (Cole 2026-10-20): the World Boss waits until month 2 or 3 after launch, when player numbers
   * make the tiers mean something; flipped here when Cole says so, never by an admin switch.
   */
  open: false as boolean,
  /** Attacks per trainer per UTC day; the best one counts. */
  tries: 3,
  /** Trainer level to attack. */
  unlockLevel: 10,
  teamMax: 5,
  /** Every creature and the boss fight at this level. */
  level: 150,
  /** Server total each tier starts at (tier 0 below the first). */
  tiers: [10_000, 30_000, 100_000, 300_000, 1_000_000, 3_000_000] as const,
  /** Coins: one hunt kill's coins at the trainer's level for every coinPer damage of their best, at most coinKillsMax kills... */
  coinPer: 25,
  coinKillsMax: 400,
  /** ...times 1 + tierBonus per server tier. */
  tierBonus: 0.25,
  /**
   * Each trainer's best counts toward the server total up to this (the coin cap, coinPer × coinKillsMax), so the tiers
   * measure how many trainers took part, not one maxed team: a top team deals about 265,000, enough alone for tier 3.
   */
  serverCap: 10_000,
  /** Revives everyone who attacked gets, by server tier reached. */
  revives: [[2, 1], [5, 2]] as const,
  /** The day's top attackers each get one key to that day's boss (BOSS_TOKEN): tradeable, so the market feels it. */
  keyPlaces: 3,
  /** Top attackers shown on the page. */
  topShown: 10,
  /** Boss HP multiple: high enough that it never falls. */
  hpMult: 1e9,
} as const;

export const WB_DAY_MS = 86_400_000;
/** UTC day number. */
export const wbDay = (now: number) => Math.floor(now / WB_DAY_MS);
/** When the day ends (the next UTC midnight). */
export const wbEndsAt = (day: number) => (day + 1) * WB_DAY_MS;
/** The boss of a day: the region bosses in turn, so a counter team pays off on its day. */
export const wbBoss = (day: number) => BOSSES[((day % BOSSES.length) + BOSSES.length) % BOSSES.length];

/** Server tier for a total: 0 below the first, up to WB.tiers.length. */
export function wbTier(total: number): number {
  let t = 0;
  for (const x of WB.tiers) if (total >= x) t++;
  return t;
}

/** Coins a trainer gets for the day: by their best attack, their level and the server's tier. */
export function wbCoins(level: number, best: number, tier: number): number {
  if (best <= 0) return 0;
  const kills = Math.min(WB.coinKillsMax, Math.max(1, Math.round(best / WB.coinPer)));
  return Math.round(killCoins(level) * kills * (1 + WB.tierBonus * tier));
}

/** Revives for the day at a server tier. */
export function wbRevives(tier: number): number {
  let n = 0;
  for (const [t, q] of WB.revives) if (tier >= t) n = q;
  return n;
}

/**
 * The key a top-WB.keyPlaces attack earns, or null: the day's boss's key, or, when the winner's own hunts don't drop
 * that key yet (trainer level below its region's tokenFrom), the highest boss key they do, Fenrir's at least. The fight
 * is at Lv 150, so a winner can be far from Typhon or Hachimata; without this the server banked keys to bosses nobody
 * can beat yet (about 7 Sacred Ropes a week for months), all used at once when the first trainers reach Yomi's boss.
 */
export function wbKey(bossId: string, rank: number, level: number): string | null {
  if (rank < 1 || rank > WB.keyPlaces) return null;
  let i = BOSSES.findIndex((b) => b.id === bossId);
  if (i < 0) return null;
  while (i > 0 && level < (REGIONS.find((r) => r.id === BOSSES[i].region)?.tokenFrom ?? 0)) i--;
  return BOSS_TOKEN[BOSSES[i].id] ?? null;
}

/** Why these creatures can't attack, or null. One of each species, at most WB.teamMax, none listed. */
export function wbTeamProblem(team: (CreatureDTO | undefined)[]): string | null {
  if (!team.length) return 'Pick at least one creature.';
  if (team.length > WB.teamMax) return `At most ${WB.teamMax} creatures.`;
  if (team.some((c) => !c)) return 'One of those creatures is no longer yours.';
  if (team.some((c) => c!.listed)) return 'A creature listed on the market can\'t fight.';
  if (new Set(team.map((c) => c!.species)).size !== team.length) return 'One of each species.';
  return null;
}

/** The boss as it fights. */
export const wbBossFighter = (bossId: string): Fighter => bossFighter(bossId, WB.level);

/** A creature as it fights the World Boss: at WB.level, its keeper bonus counting as against any boss. */
export const wbFighter = (c: CreatureDTO, halls?: readonly string[]): Fighter => creatureFighter(c.level === WB.level ? c : { ...c, level: WB.level }, halls);

/** One attack: the damage dealt before the team falls or the clock runs out, and the blow log. */
export function wbAttack(team: CreatureDTO[], bossId: string, rng: Rng, halls?: readonly string[]): { dmg: number; teamMaxHp: Record<number, number>; byId: Record<number, number>; fell: number[]; crits: number; log: BossResult['log']; bossMaxHp: number } {
  const teamMaxHp: Record<number, number> = {};
  const fs = team.map((c) => { const f = wbFighter(c, halls); teamMaxHp[c.id] = f.stats.hp; return { id: c.id, f }; });
  const log: BossResult['log'] = [];
  const r = duel(fs, wbBossFighter(bossId), rng, log, WB.hpMult);
  const byId: Record<number, number> = {};
  const fell: number[] = [];
  let crits = 0;
  for (const x of log) {
    if (x.by === 'team') { byId[x.id] = (byId[x.id] ?? 0) + x.dmg; if (x.crit) crits++; }
    else if (x.hp <= 0 && !fell.includes(x.id)) fell.push(x.id);
  }
  return { dmg: Math.round(r.bossMaxHp - r.bossHp), teamMaxHp, byId, fell, crits, log, bossMaxHp: r.bossMaxHp };
}

// ---------- what the page gets ----------
export interface WbFace { species: string; level: number; aura?: boolean }
export interface WbRow { rank: number; name: string; outfit: string; best: number; team: WbFace[]; me?: boolean }

export interface WbYesterday {
  day: number;
  boss: string;
  total: number;
  tier: number;
  attackers: number;
  top: WbRow[];
  /** What you got, when you attacked. */
  mine?: { best: number; rank: number; coins: number; revives: number; key?: string };
}

export interface WbDTO {
  day: number;
  boss: string;
  endsAt: number;
  /** Open (WB.open): attacks count. Closed, it shows "Coming soon"; only test accounts can try, and nothing counts. */
  open: boolean;
  unlocked: boolean;
  /** Admin or playtest account: attacks never count. */
  test: boolean;
  total: number;
  attackers: number;
  tier: number;
  mine: { best: number; tries: number; ids: number[]; rank: number } | null;
  top: WbRow[];
  yesterday: WbYesterday | null;
}

/** One attack's result as the page shows it. */
export interface WbResult {
  boss: string;
  dmg: number;
  best: number;
  newBest: boolean;
  counted: boolean;
  triesLeft: number;
  /** Damage by creature id, and the order they were knocked out. */
  byId: Record<number, number>;
  fell: number[];
  crits: number;
  /** The creatures as they fought, in team order. */
  team: (WbFace & { id: number })[];
  /** The fight, for the replay on the board (absent in older results). */
  log?: BossResult['log'];
  bossMaxHp?: number;
  teamMaxHp?: Record<number, number>;
}
